import React from "react";
import {
    CartesianGrid,
    Customized,
    Legend,
    Line,
    LineChart,
    Rectangle,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    Dot,
} from "recharts";
import { format, isValid } from "date-fns";
import {
    detectChartGranularity,
    formatChartTooltipDate,
    formatChartAxisDate,
} from "src/utils/chart-date-formatter";
import styled from "styled-components";
import { Backdrop, Box, Divider, Grid, IconButton, Modal, Stack, Typography } from "@mui/material";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import TooltipMui from "@mui/material/Tooltip";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import ChartColors from "src/components/chart-colors";
import getRandomColor from "src/components/random-color-generator";
import { resolveObjPathString } from "src/utils/resolve-path";
import timeSplitter from "src/utils/time-splitter";
import TicketsStateDurationTable from "src/components/tickets-state-duration-table";
import { resolveName } from "src/utils/templating";
import { calculateLineInterval } from "src/components/calculate-tick-interval";

const HeaderStack = styled(Stack)`
    width: 100%;
    background-color: #5b5b5b;
    padding: 4px 8px 2px 16px;
    border-radius: 14px 14px 0 0;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;

const TooltipContainer = styled.div`
    background-color: ${({ theme }) => theme.palette.background.tooltip};
    padding: 0.5rem;
    min-width: 10rem;
    align-items: center;
    flex-direction: column;
    border-radius: 6px;
`;

const TooltipTitle = styled.p`
    margin-top: 0;
    margin-bottom: 0.5rem;
`;

const TooltipContent = styled.p`
    font-size: 12px;
    margin: 0;
    color: ${({ color }) => color};
`;

const ClickBox = styled(Box)`
    background-color: #333333;
    border-radius: 14px;
    margin: 12px;
    padding-left: 16px;
    padding-top: 1px;
    max-height: 400px;
    overflow-y: auto;
    overflow-x: hidden;
    height: calc(95vh - 450px);
`;

const InfoText = styled.p`
    font-weight: 300;
    font-size: 12px;
    margin: 0;
`;

const CustomizedDot = (referenceLine) => (props) => {
    if (!referenceLine) return <Dot {...props} />;
    const { value, name } = props;
    if (referenceLine.axisId && name !== referenceLine.axisId) return <Dot {...props} />;
    if (referenceLine.target === "below") {
        if (referenceLine.value > value)
            return <Dot {...props} stroke="green" strokeWidth={3} r={4} />;
        return <Dot {...props} stroke="orange" strokeWidth={3} r={4} />;
    }
    if (referenceLine.value > value)
        return <Dot {...props} stroke="orange" strokeWidth={3} r={4} />;
    return <Dot {...props} stroke="green" strokeWidth={3} r={4} />;
};

const CustomizedRectangle = (props) => {
    const { formattedGraphicalItems } = props;
    const firstSeries = formattedGraphicalItems[0];
    const secondSeries = formattedGraphicalItems[1];
    return firstSeries?.props?.points.map((firstSeriesPoint, index) => {
        const secondSeriesPoint = secondSeries?.props?.points[index];
        const yDifference = firstSeriesPoint.y - secondSeriesPoint.y;
        const calculateHeight = () => {
            if (yDifference === 0 && firstSeriesPoint.y + secondSeriesPoint.y === 368) {
                return 0;
            }
            if (yDifference === 0) {
                return 10;
            }
            return yDifference;
        };
        return (
            <Rectangle
                key={firstSeriesPoint.payload.name}
                width={20}
                height={calculateHeight()}
                x={secondSeriesPoint.x - 10}
                y={secondSeriesPoint.y}
                fill="#009688"
            />
        );
    });
};

const getValue = (value, unit) => {
    if (unit === "number") return value;
    if (unit === "percentage") return `${value}%`;
    if (unit === "duration")
        return `${timeSplitter(value, "variable-accuracy")} ${timeSplitter(value, "text")}`;
    if (unit === "workSeconds") return timeSplitter(value, "work-seconds");
    return value;
};

const CustomTooltip = ({ active, payload, expandable, granularity }) => {
    if (active && payload && payload.length) {
        return (
            <TooltipContainer>
                <TooltipTitle>
                    {isValid(new Date(payload[0].payload.datetime)) &&
                        formatChartTooltipDate(payload[0].payload.datetime, granularity)}
                </TooltipTitle>
                {payload.map((entry) => {
                    return (
                        <Stack direction="row" spacing={0.5}>
                            <TooltipContent color={entry.color}>{entry.name}: </TooltipContent>
                            <TooltipContent> {getValue(entry.value, entry.unit)}</TooltipContent>
                        </Stack>
                    );
                })}
                {expandable && (
                    <Stack
                        direction="column"
                        mt={1}
                        style={{ width: "100%", backgroundColor: "#303030", borderRadius: "8px" }}
                    >
                        <TooltipContent style={{ fontWeight: 300, margin: "8px" }}>
                            Click for details
                        </TooltipContent>
                    </Stack>
                )}
            </TooltipContainer>
        );
    }
    return null;
};

const hasOnlyDatetime = (data) => {
    if (!Array.isArray(data)) {
        return false;
    }
    return data.every((item) => {
        return (
            typeof item === "object" &&
            Object.keys(item).length === 1 &&
            "datetime" in item &&
            typeof item.datetime === "string"
        );
    });
};

const TemplateLineChart = ({ data, title, parameters, context, filter, orgMeetingAvg }) => {
    const [hiddenSeries, setHiddenSeries] = React.useState([]);
    const [clickedPoint, setClickedPoint] = React.useState(null);
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [details, setDetails] = React.useState();
    const [interval, setInterval] = React.useState(0);
    const handleColors = (index) => {
        return ChartColors[index] || getRandomColor();
    };

    const handleLegendClick = (dataKey) => {
        if (hiddenSeries.includes(dataKey)) {
            setHiddenSeries(hiddenSeries.filter((el) => el !== dataKey));
        } else {
            setHiddenSeries((prev) => [...prev, dataKey]);
        }
    };

    const handleChartClick = (e) => {
        const isLegendClick = Object.keys(e).length === 0;
        if (!isLegendClick) {
            setClickedPoint(e);
            setIsExpanded(true);
        }
    };

    const handleClose = () => {
        setIsExpanded(false);
    };

    const transform = () => {
        const transformedParameters = {
            yValues: {},
        };

        const granularity = detectChartGranularity(data);
        const xParams = parameters.xValues.map((xValue) => xValue.dataKey);

        const yKeys = Array.from(new Set((data || []).flatMap((item) => Object.keys(item)))).filter(
            (key) => !xParams.includes(key)
        );

        const filteredData = filter
            ? (data || []).filter((d) => d[filter.property] === filter.value)
            : data;
        const transformedData = (filteredData || []).map((item) => {
            const dataPoint = {};
            parameters.xValues.forEach((xValue) => {
                dataPoint[xValue.dataKey] = item[xValue.dataKey];
                if (xValue.type === "datetime") {
                    dataPoint.date =
                        isValid(new Date(item[xValue.dataKey])) &&
                        formatChartAxisDate(item[xValue.dataKey], granularity);
                }
                if (xValue.type === "weekday") {
                    dataPoint[xValue.dataKey] =
                        item[xValue.dataKey].charAt(0).toUpperCase() +
                        item[xValue.dataKey].slice(1);
                }
            });

            if (parameters.yValues) {
                parameters.yValues.forEach((yValue) => {
                    const dataKey = resolveName(yValue.dataKey, context);
                    if (dataKey.includes("[*]")) {
                        let dataSubset = item;
                        let [path] = dataKey.split("[*]");
                        if (path) {
                            path = path.endsWith(".") ? path.substring(0, path.length - 1) : path;
                            dataSubset = resolveObjPathString(item, path);
                        }
                        if (dataSubset)
                            Object.keys(dataSubset).forEach((key) => {
                                dataPoint[key] = dataSubset[key];
                                if (parameters.xValues.find((xValue) => xValue.dataKey === key))
                                    return;
                                if (!transformedParameters.yValues[key])
                                    transformedParameters.yValues[key] = {
                                        ...yValue,
                                        dataKey: key,
                                        name: key,
                                    };
                            });
                    } else {
                        dataPoint[dataKey] = resolveObjPathString(item, dataKey);
                        if (!transformedParameters.yValues[dataKey])
                            transformedParameters.yValues[dataKey] = {
                                ...yValue,
                                dataKey,
                                name: resolveName(yValue.name, context),
                            };
                    }
                });
            } else {
                yKeys.forEach((key) => {
                    dataPoint[key] = item[key] !== undefined ? item[key] : null;
                    transformedParameters.yValues[key] = {
                        dataKey: key,
                        name: key,
                    };
                });
            }

            if (parameters.expandable) {
                dataPoint.details = item.details;
            }

            return dataPoint;
        });
        return {
            transformedData,
            transformedParameters,
            granularity,
        };
    };

    const { transformedData, transformedParameters, granularity } = React.useMemo(transform, [
        data && JSON.stringify(data),
        parameters && JSON.stringify(parameters),
        context && JSON.stringify(context),
        filter,
    ]);

    const meetingsRefLines = React.useMemo(() => {
        if (!orgMeetingAvg?.avgMeetingsTimePerDaySeconds) return null;
        if (!transformedParameters.yValues.meetings) return null;
        const scaleMap = { month: 30, week: 7 };
        const scale = scaleMap[granularity] ?? 1;
        return {
            avg: orgMeetingAvg.avgMeetingsTimePerDaySeconds * scale,
            threshold: orgMeetingAvg.thresholdPerDaySeconds * scale,
        };
    }, [orgMeetingAvg, transformedParameters.yValues, granularity]);

    React.useEffect(() => {
        if (clickedPoint?.activeLabel) {
            for (let i = 0; i < parameters.xValues.length; i += 1) {
                const xValue = parameters.xValues[i];
                let point;
                if (xValue.type === "datetime") {
                    point = transformedData.find((d) => d.date === clickedPoint.activeLabel);
                    if (point) {
                        setDetails(point?.details);
                        break;
                    }
                }
                if (xValue.type === "weekday") {
                    point = transformedData.find((d) => d.weekday === clickedPoint.activeLabel);
                    if (point) {
                        setDetails(point?.details);
                        break;
                    }
                }
            }
        }
    }, [clickedPoint && clickedPoint.activeLabel]);

    React.useEffect(() => {
        setInterval(calculateLineInterval());
    }, [data && JSON.stringify(data)]);

    if (!data || (data && data.length === 0)) return <NoDataPlaceholder variant="bar" />;
    if (hasOnlyDatetime(data)) return <NoDataPlaceholder variant="bar" />;

    const chart = (
        <ResponsiveContainer width="100%" height={isExpanded ? 400 : "100%"}>
            {!isExpanded && parameters.expandable && (
                <div style={{ position: "relative" }}>
                    <IconButton
                        size="small"
                        onClick={() => setIsExpanded(true)}
                        aria-label="expand chart"
                        color="secondary"
                        target="_blank"
                        style={{
                            marginRight: "23px",
                            marginTop: "0px",
                            position: "absolute",
                            right: 0,
                            zIndex: 100,
                        }}
                    >
                        <TooltipMui title="Expand/Details">
                            <FitScreenIcon />
                        </TooltipMui>
                    </IconButton>
                </div>
            )}
            <LineChart
                width={500}
                height={300}
                style={{ zIndex: 99 }}
                data={transformedData}
                margin={{
                    top: 5,
                    right: 25,
                    left: 20,
                    bottom: 5,
                }}
                onClick={(e) => handleChartClick(e)}
            >
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <YAxis
                    width={5}
                    tick={false}
                    opacity={0}
                    domain={
                        meetingsRefLines
                            ? [0, (dataMax) => Math.max(dataMax, meetingsRefLines.threshold * 1.1)]
                            : ["auto", "auto"]
                    }
                />
                {parameters.xValues &&
                    parameters.xValues.map((xValue) => (
                        <XAxis
                            dataKey={xValue.type === "datetime" ? "date" : xValue.dataKey}
                            interval={interval}
                            tick={{
                                fill: "#EAF0F4",
                                opacity: "0.3",
                                fontSize: "12px",
                                fontWeight: "300",
                            }}
                            tickFormatter={(tick) => tick.slice(0, 10)}
                        />
                    ))}
                <Tooltip
                    cursor={{ opacity: "0.2" }}
                    content={
                        <CustomTooltip
                            expandable={parameters?.expandable}
                            granularity={granularity}
                        />
                    }
                />
                {!parameters.hideLegend && (
                    <Legend
                        verticalAlign="bottom"
                        height={16}
                        wrapperStyle={{ fontSize: "11px", bottom: "6px" }}
                        onClick={(props) => handleLegendClick(props.dataKey)}
                    />
                )}
                {clickedPoint !== null && isExpanded && (
                    <ReferenceLine x={clickedPoint.activeLabel} stroke="#5ac36b" />
                )}
                {parameters.referenceLine && (
                    <ReferenceLine
                        {...(parameters.referenceLine.axis &&
                            parameters.referenceLine.axis === "x" && {
                                x: parameters.referenceLine.value,
                            })}
                        {...(parameters.referenceLine.axis &&
                            parameters.referenceLine.axis === "y" && {
                                y: parameters.referenceLine.value,
                            })}
                        stroke={parameters.referenceLine.stroke}
                        strokeDasharray={parameters.referenceLine.strokeDasharray}
                        label={parameters.referenceLine.label}
                        position={parameters.referenceLine.position}
                        yAxisId={parameters.referenceLine.axisId}
                    />
                )}
                {meetingsRefLines && (
                    <>
                        <ReferenceLine
                            y={meetingsRefLines.avg}
                            stroke="#60d297"
                            strokeDasharray="4 4"
                            label={{
                                value: "Org avg",
                                position: "insideTopRight",
                                fill: "#60d297",
                                fontSize: 11,
                            }}
                        />
                        <ReferenceLine
                            y={meetingsRefLines.threshold}
                            stroke="#f59e0b"
                            strokeDasharray="4 4"
                            label={{
                                value: "High load",
                                position: "insideTopRight",
                                fill: "#f59e0b",
                                fontSize: 11,
                            }}
                        />
                    </>
                )}
                {Object.values(transformedParameters.yValues)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((yValue, index) => (
                        <>
                            <Line
                                yAxisId={yValue.axis?.id}
                                key={yValue.dataKey}
                                type={yValue.type || "monotone"}
                                dataKey={yValue.dataKey}
                                name={yValue.name}
                                stroke={yValue.stroke || handleColors(index)}
                                dot={
                                    parameters.referenceLine
                                        ? CustomizedDot(parameters.referenceLine)
                                        : yValue.dot || true
                                }
                                activeDot={yValue.activeDot}
                                connectNulls={
                                    typeof yValue.connectNulls === "boolean"
                                        ? yValue.connectNulls
                                        : true
                                }
                                hide={hiddenSeries.includes(yValue.dataKey)}
                                style={yValue.style}
                                unit={yValue.unit}
                            />
                            {yValue.axis && (
                                <YAxis
                                    yAxisId={yValue.axis?.id}
                                    orientation={yValue.axis?.orientation}
                                    tick={false}
                                    axisLine={false}
                                    tickLine={false}
                                    width={0}
                                    domain={yValue.axis?.domain}
                                />
                            )}
                        </>
                    ))}
                {parameters.customized && parameters.customized.includes("rectangle") && (
                    <Customized component={CustomizedRectangle} />
                )}
            </LineChart>
        </ResponsiveContainer>
    );

    if (isExpanded) {
        return (
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={isExpanded}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -51%)",
                        bgcolor: "background.paper",
                        borderRadius: 4,
                        boxShadow: 24,
                        width: "90%",
                        maxHeight: "95vh",
                    }}
                >
                    <HeaderStack>
                        {title && (
                            <Typography id="transition-modal-title" style={{ fontSize: "14px" }}>
                                {title}
                            </Typography>
                        )}
                    </HeaderStack>
                    {chart}
                    {clickedPoint &&
                        clickedPoint &&
                        clickedPoint.activePayload &&
                        clickedPoint.activePayload[0] &&
                        clickedPoint.activePayload[0].payload && (
                            <ClickBox>
                                <Grid container direction="row" spacing={2}>
                                    <Grid item mr={1.5} style={{ width: "230px" }}>
                                        <p>
                                            {isValid(
                                                new Date(
                                                    clickedPoint.activePayload[0].payload.datetime
                                                )
                                            ) &&
                                                `${format(
                                                    new Date(
                                                        clickedPoint.activePayload[0].payload.datetime
                                                    ),
                                                    "EEEE d MMMM yyyy"
                                                )}`}
                                        </p>
                                        <Stack direction="column" spacing={1} mb={2}>
                                            {clickedPoint.activePayload.map((payload) => {
                                                if (payload.value === null) return null;
                                                return (
                                                    <Box key={payload.name}>
                                                        <InfoText>
                                                            {payload.name}:{" "}
                                                            {getValue(payload.value, payload.unit)}
                                                        </InfoText>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </Grid>
                                    <Divider orientation="vertical" variant="middle" flexItem />

                                    <Grid item md={9}>
                                        <TicketsStateDurationTable
                                            data={details}
                                            hiddenSeries={hiddenSeries}
                                            formats={parameters?.details}
                                        />
                                    </Grid>
                                </Grid>
                            </ClickBox>
                        )}
                </Box>
            </Modal>
        );
    }
    return chart;
};

export default TemplateLineChart;
